/**
 * The offline punch queue (rebuilt Oct 29 2026).
 *
 * A punch made while the device is offline is stored HERE, on the device, with:
 * - the device time it was made at (ISO with the device's offset),
 * - a client punch id (random, made once), so a replay can never book twice,
 * - who punched: the employee picked on the shared clock (+ their PIN, encrypted with a
 *   non-extractable device key; the server checks it at sync and the cipher is wiped after),
 *   or the staff member's own session (no PIN),
 * - which session may send it (`auth`) and for which restaurant.
 *
 * When the device is back online the queue goes to POST /api/timeclock/v2/punches/sync
 * (oldest first). The server answers per punch: applied (on the clock), review (kept for a
 * manager: out of order, clock-out without clock-in, device clock ahead), rejected (wrong
 * PIN, unknown employee), retry (PIN locked right now: sent again later). No token is ever
 * stored here: the headers are the session's own at sync time.
 *
 * Its own IndexedDB database (chain29-timeclock) so the service worker's older
 * asterpos-offline database (version 1) is never upgraded under it.
 */
import { getAccount, accountHeaders } from '@/lib/pwaAccount';
import { getBranchAuthHeaders } from '@/utils/branchApi';
import { timeClockApi } from './api';
import { isoWithOffset } from './time';

const DB_NAME = 'chain29-timeclock';
const DB_VERSION = 1;
const PUNCHES = 'punches';
const KEYS = 'keys';
export const QUEUE_CHANGED = 'chain29-timeclock-queue-changed';
const DONE_KEEP_MS = 7 * 24 * 3600 * 1000;

let dbPromise = null;

function openDb() {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('This browser cannot keep punches offline'));
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(PUNCHES)) {
          const store = db.createObjectStore(PUNCHES, { keyPath: 'clientPunchId' });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('occurredAtMs', 'occurredAtMs', { unique: false });
        }
        if (!db.objectStoreNames.contains(KEYS)) db.createObjectStore(KEYS);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => { dbPromise = null; reject(req.error); };
    });
  }
  return dbPromise;
}

function tx(storeName, mode, fn) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(storeName, mode);
    const store = t.objectStore(storeName);
    let out;
    Promise.resolve(fn(store)).then((v) => { out = v; });
    t.oncomplete = () => resolve(out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

const req = (r) => new Promise((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error); });

function announce() {
  try { window.dispatchEvent(new Event(QUEUE_CHANGED)); } catch (e) { /* not in a window */ }
}

export function newClientPunchId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `tc-${crypto.randomUUID()}`;
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return `tc-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

export function deviceId() {
  try {
    let id = localStorage.getItem('chain29_timeclock_device');
    if (!id) {
      id = newClientPunchId().replace('tc-', 'dev-').slice(0, 40);
      localStorage.setItem('chain29_timeclock_device', id);
    }
    return id;
  } catch (e) {
    return null;
  }
}

// ---------------------------------------------------------------- the PIN, encrypted at rest

async function deviceKey() {
  if (!crypto?.subtle) throw new Error('Offline punches with a PIN need a secure (https) connection');
  const existing = await tx(KEYS, 'readonly', (s) => req(s.get('pin-key')));
  if (existing) return existing;
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  await tx(KEYS, 'readwrite', (s) => req(s.put(key, 'pin-key')));
  return key;
}

async function sealPin(pin) {
  const key = await deviceKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(String(pin)));
  return { iv, data };
}

async function openPin(sealed) {
  if (!sealed) return null;
  const key = await deviceKey();
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: sealed.iv }, key, sealed.data);
  return new TextDecoder().decode(plain);
}

// ---------------------------------------------------------------- sessions

function jwtPayload(token) {
  try {
    return JSON.parse(atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch (e) {
    return null;
  }
}

/** The session a queued punch is sent with, and the restaurant / staff it speaks for. */
export function sessionFor(auth) {
  if (auth === 'account') {
    const account = getAccount();
    return account ? { headers: () => accountHeaders(), restaurantId: Number(account.restaurant.id) } : null;
  }
  if (auth === 'backoffice') {
    const token = localStorage.getItem('backoffice_token');
    if (!token) return null;
    const picked = localStorage.getItem('backoffice_active_restaurant');
    return { headers: getBranchAuthHeaders, restaurantId: Number(picked || jwtPayload(token)?.restaurant_id) };
  }
  if (auth === 'staff') {
    const token = localStorage.getItem('pos_token');
    const p = token && jwtPayload(token);
    if (!p || p.typ !== 'staff') return null;
    return { headers: () => ({ Authorization: `Bearer ${token}` }), restaurantId: Number(p.restaurant_id),
             staffId: Number(p.user_id || p.sub) };
  }
  return null;
}

// ---------------------------------------------------------------- the queue

/**
 * Keep a punch on the device. `who`: { staffId, staffName, phone?, pin? } — the PIN only for
 * a shared clock, sealed before it is stored.
 */
export async function queuePunch({ auth, restaurantId, who, action, breakPaid = false, roleId = null,
                                   clientPunchId = null, at = null }) {
  // a punch whose online request may have reached the server keeps ITS id and time: the
  // replay then answers the same, never a second booking
  const now = at instanceof Date ? at : new Date();
  const record = {
    clientPunchId: clientPunchId || newClientPunchId(),
    auth,
    restaurantId: Number(restaurantId),
    staffId: who?.staffId ?? null,
    staffName: who?.staffName || null,
    phone: who?.phone || null,
    pinSealed: who?.pin ? await sealPin(who.pin) : null,
    action,
    breakPaid: !!breakPaid,
    roleId,
    occurredAt: isoWithOffset(now),
    occurredAtMs: now.getTime(),
    status: 'pending',
    attempts: 0,
    message: null,
    createdAt: now.getTime(),
  };
  await tx(PUNCHES, 'readwrite', (s) => req(s.put(record)));
  announce();
  // background sync is optional (the page syncs when online); never awaited: `ready` never
  // resolves on a page without a service worker
  try {
    navigator.serviceWorker?.getRegistration?.()
      .then((reg) => reg?.sync?.register('sync-timeclock'))
      .catch(() => {});
  } catch (e) { /* no service worker API */ }
  const { pinSealed, ...safe } = record;
  return safe;
}

export async function listPunches({ limit = 200 } = {}) {
  const all = await tx(PUNCHES, 'readonly', (s) => req(s.getAll()));
  return (all || []).sort((a, b) => b.occurredAtMs - a.occurredAtMs).slice(0, limit)
    .map(({ pinSealed, ...safe }) => safe);
}

export async function pendingCount() {
  try {
    return await tx(PUNCHES, 'readonly', (s) => req(s.index('status').count('pending')));
  } catch (e) {
    return 0;
  }
}

async function update(clientPunchId, changes) {
  await tx(PUNCHES, 'readwrite', async (s) => {
    const rec = await req(s.get(clientPunchId));
    if (rec) await req(s.put({ ...rec, ...changes }));
  });
}

let syncing = null;

/**
 * Send every pending punch with its own session, oldest first. Resolves to
 * { sent, applied, review, rejected, retry, waiting } (waiting = no session to send it yet).
 */
export function syncPunches() {
  if (syncing) return syncing;
  syncing = (async () => {
    const totals = { sent: 0, applied: 0, review: 0, rejected: 0, retry: 0, waiting: 0 };
    let pending;
    try {
      pending = await tx(PUNCHES, 'readonly', (s) => req(s.index('status').getAll('pending')));
    } catch (e) {
      return totals;
    }
    pending.sort((a, b) => a.occurredAtMs - b.occurredAtMs);
    const groups = {};
    for (const rec of pending) {
      const key = `${rec.auth}:${rec.restaurantId}`;
      (groups[key] = groups[key] || []).push(rec);
    }
    for (const recs of Object.values(groups)) {
      const session = sessionFor(recs[0].auth);
      if (!session || session.restaurantId !== recs[0].restaurantId
          || (recs[0].auth === 'staff' && recs.some((r) => r.staffId !== session.staffId))) {
        totals.waiting += recs.length;
        for (const r of recs) await update(r.clientPunchId, { message: 'Waiting for this restaurant\'s sign-in on this device' });
        continue;
      }
      const body = [];
      for (const r of recs) {
        let pin = null;
        try { pin = await openPin(r.pinSealed); } catch (e) { pin = null; }
        body.push({
          client_punch_id: r.clientPunchId, action: r.action, occurred_at: r.occurredAt,
          break_paid: r.breakPaid, role_id: r.roleId,
          ...(r.auth === 'staff' ? {} : { staff_id: r.staffId ?? undefined, phone: r.staffId ? undefined : r.phone, pin }),
        });
      }
      for (let i = 0; i < body.length; i += 100) {
        const chunk = body.slice(i, i + 100);
        let answer;
        try {
          answer = await timeClockApi(session.headers).sync(deviceId(), chunk);
        } catch (e) {
          for (const p of chunk) {
            const rec = recs.find((r) => r.clientPunchId === p.client_punch_id);
            await update(p.client_punch_id, { attempts: (rec?.attempts || 0) + 1, message: e.message });
          }
          totals.waiting += chunk.length;
          continue;
        }
        totals.sent += chunk.length;
        for (const result of answer.results || []) {
          const id = result.client_punch_id;
          if (!id) continue;
          const status = result.status;
          totals[status] = (totals[status] || 0) + 1;
          if (status === 'retry') {
            await update(id, { message: result.message, attempts: 1 + (recs.find((r) => r.clientPunchId === id)?.attempts || 0) });
          } else {
            // decided by the server: the PIN cipher is wiped, the answer is kept for the device's history
            await update(id, { status, message: result.message || result.review_reason || null, pinSealed: null,
                               syncedAt: Date.now(), punchId: result.punch_id || null });
          }
        }
      }
    }
    await cleanup();
    announce();
    return totals;
  })().finally(() => { syncing = null; });
  return syncing;
}

export async function cleanup() {
  const cutoff = Date.now() - DONE_KEEP_MS;
  try {
    await tx(PUNCHES, 'readwrite', async (s) => {
      const all = await req(s.getAll());
      for (const r of all) {
        if (r.status !== 'pending' && (r.syncedAt || r.createdAt) < cutoff) await req(s.delete(r.clientPunchId));
      }
    });
  } catch (e) { /* nothing to clean */ }
}

/** Sync when the device comes back online, when the service worker asks, and every 30 s. */
let started = false;
export function startAutoSync() {
  if (started || typeof window === 'undefined') return;
  started = true;
  const go = () => { if (navigator.onLine) syncPunches().catch(() => {}); };
  window.addEventListener('online', go);
  navigator.serviceWorker?.addEventListener?.('message', (e) => { if (e.data?.type === 'timeclock-sync') go(); });
  setInterval(async () => { if (navigator.onLine && (await pendingCount()) > 0) go(); }, 30000);
  go();
}

export default { queuePunch, listPunches, pendingCount, syncPunches, startAutoSync, deviceId, newClientPunchId };
